<template>
  <v-flex xs12>
    <h3 class="pb-2 mr-2 d-inline-block">
      <span class="mr-1" style="font-weight: 700;">Step 2</span>-
      Select CSV to upload
    </h3>
    <v-flex xs12 px-4 py-4 mb-4 class="white-bg elevation-2 rounded-3">
      <v-btn
        color="secondary"
        class="text-none ma-0"
        depressed
        :loading="isSelecting"
        @click="onButtonClick"
      >
        <v-icon left>cloud_upload</v-icon>
        {{ buttonText }}
      </v-btn>
      <input
        ref="uploader"
        class="d-none"
        type="file"
        accept=".csv"
        @change="onFileChanged"
      />
      <p v-if="parseError" class="red--text mt-3 mb-0">{{ parseError }}</p>
    </v-flex>

    <v-flex xs12 v-if="mappings.length > 0">
      <h3 class="pb-2 mr-2 d-inline-block">
        <span class="mr-1" style="font-weight: 700;">Step 3</span>-
        Match CSV columns to template fields
      </h3>
      <v-flex xs12 px-4 py-4 mb-4 class="white-bg elevation-2 rounded-3">
        <p class="mb-3">
          Columns were matched automatically where possible. Use Exclude to skip a
          column, or change the mapping in the dropdown.
        </p>
        <csv-field-mapping
          :mappings="mappings"
          :template-fields="templateFields"
          @change-mapping="onMappingChange"
        />
      </v-flex>
    </v-flex>

    <v-flex xs12 v-if="mappedNames.length > 0">
      <h3 class="pb-2 mr-2 d-inline-block">
        <span class="mr-1" style="font-weight: 700;">Step 4</span>-
        From the mapped fields, which ones are primary keys?
      </h3>
      <v-flex xs12 px-4 py-3 mb-4 class="white-bg elevation-2 rounded-3">
        <v-chip
          color="secondary"
          text-color="white"
          v-for="(fieldName, index) in mappedNames"
          :key="index"
        >
          <v-switch
            :input-value="!!primarySelected[fieldName]"
            class="mt-2"
            @change="togglePrimary(fieldName, $event)"
          />
          {{ fieldName }}
        </v-chip>
      </v-flex>
    </v-flex>
  </v-flex>
</template>

<script>
import Papa from "papaparse"
import { mapState } from "vuex"
import CsvFieldMapping from "@/components/import/CsvFieldMapping.vue"
import {
  collectAtomicFields,
  matchCsvHeaders,
  mappedFieldNames,
  mappedFields,
  suggestDefaultPrimaries,
  applyMappingOverride,
} from "@/helpers/csvFieldMatch"

export default {
  name: "atomic-import",
  components: { CsvFieldMapping },
  data: function () {
    return {
      isSelecting: false,
      selectedFile: null,
      parseError: null,
      mappings: [],
      primarySelected: {},
      csvRows: [],
    }
  },
  computed: {
    ...mapState({
      caseType: function (state) {
        return state.caseTypes.current
      },
    }),
    templateFields: function () {
      const schema = this.caseType && this.caseType.currentSchema
      return collectAtomicFields(schema)
    },
    mappedNames: function () {
      return mappedFieldNames(this.mappings)
    },
    buttonText: function () {
      return this.selectedFile
        ? this.selectedFile.name
        : "Select CSV from your computer"
    },
  },
  watch: {
    mappings: function () {
      this.emitSelections()
    },
    primarySelected: {
      deep: true,
      handler: function () {
        this.emitSelections()
      },
    },
  },
  methods: {
    onButtonClick: function () {
      this.isSelecting = true
      window.addEventListener(
        "focus",
        function () {
          this.isSelecting = false
        }.bind(this),
        { once: true },
      )
      this.$refs.uploader.click()
    },
    onFileChanged: function (e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.selectedFile = file
      this.parseError = null
      this.parseCsv(file)
    },
    parseCsv: function (file) {
      const self = this
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: function (results) {
          if (results.errors && results.errors.length > 0) {
            self.parseError = results.errors[0].message || "Unable to parse CSV"
            self.mappings = []
            self.csvRows = []
            self.emitReady(false)
            return
          }
          const headers = results.meta && results.meta.fields ? results.meta.fields : []
          const rows = results.data || []
          const sample = rows.length > 0 ? rows[0] : {}
          self.csvRows = rows
          self.mappings = matchCsvHeaders(headers, self.templateFields, sample)
          const names = mappedFieldNames(self.mappings)
          const suggested = suggestDefaultPrimaries(
            names,
            self.caseType && self.caseType.uniqueIdentifierField,
          )
          const primaries = {}
          names.forEach(function (name) {
            primaries[name] = suggested.indexOf(name) >= 0
          })
          self.primarySelected = primaries
          self.emitSelections()
        },
        error: function (err) {
          self.parseError = (err && err.message) || "Unable to parse CSV"
          self.mappings = []
          self.csvRows = []
          self.emitReady(false)
        },
      })
    },
    onMappingChange: function (payload) {
      this.mappings = applyMappingOverride(
        this.mappings,
        payload.index,
        payload.fieldName,
        this.templateFields,
      )
      const names = mappedFieldNames(this.mappings)
      const next = {}
      names.forEach(function (name) {
        next[name] = !!this.primarySelected[name]
      }.bind(this))
      if (
        Object.keys(next).every(function (k) {
          return !next[k]
        })
      ) {
        const suggested = suggestDefaultPrimaries(
          names,
          this.caseType && this.caseType.uniqueIdentifierField,
        )
        suggested.forEach(function (name) {
          next[name] = true
        })
      }
      this.primarySelected = next
    },
    togglePrimary: function (fieldName, value) {
      this.$set(this.primarySelected, fieldName, !!value)
    },
    emitSelections: function () {
      const fields = mappedFields(this.mappings, this.templateFields)
      const primaries = []
      Object.keys(this.primarySelected).forEach(function (key) {
        if (this.primarySelected[key]) primaries.push(key)
      }.bind(this))
      this.$emit("update-import-data", { name: "fields", value: fields })
      this.$emit("update-import-data", { name: "primaries", value: primaries })
      this.$emit("update-import-data", { name: "mappings", value: this.mappings })
      this.$emit("update-import-data", { name: "csvRows", value: this.csvRows })
      this.$emit("update-import-data", {
        name: "selectedFile",
        value: this.selectedFile,
      })
      this.emitReady(fields.length > 0 && primaries.length > 0)
    },
    emitReady: function (ready) {
      this.$emit("ready-to-upload", ready)
    },
  },
}
</script>
